/**
 * Comparator Utilities
 *
 * Pure functions for sorting comparisons with deterministic tie-breaks.
 * Used by client-side sorting in comparisons list.
 */

import type { Comparison } from '../services/comparison';

export type ComparisonSortOption = 'recent' | 'oldest' | 'favorites' | 'title' | 'score';

/**
 * Sort by createdAt descending (newest first)
 * Tie-break: Title A-Z
 */
export function byCreatedAtDesc(a: Comparison, b: Comparison): number {
  const dateA = new Date(a.createdAt).getTime();
  const dateB = new Date(b.createdAt).getTime();
  if (dateB !== dateA) {
    return dateB - dateA;
  }
  return byTitleAZ(a, b);
}

/**
 * Sort by createdAt ascending (oldest first)
 * Tie-break: Title A-Z
 */
export function byCreatedAtAsc(a: Comparison, b: Comparison): number {
  const dateA = new Date(a.createdAt).getTime();
  const dateB = new Date(b.createdAt).getTime();
  if (dateA !== dateB) {
    return dateA - dateB;
  }
  return byTitleAZ(a, b);
}

/**
 * Sort by title A-Z (case and diacritics insensitive)
 * Uses custom title from description field (AI-generated), falls back to title field
 * Tie-break: Recent first
 */
export function byTitleAZ(a: Comparison, b: Comparison): number {
  // Use description (custom title) if available, otherwise fall back to title
  const titleA = (a.description || a.title || 'Untitled').toLowerCase();
  const titleB = (b.description || b.title || 'Untitled').toLowerCase();

  // Use localeCompare for proper case/diacritics handling
  const comparison = titleA.localeCompare(titleB, 'en', { sensitivity: 'base' });
  if (comparison !== 0) {
    return comparison;
  }

  // Tie-break: recent first
  const dateA = new Date(a.createdAt).getTime();
  const dateB = new Date(b.createdAt).getTime();
  return dateB - dateA;
}

/**
 * Sort by winner score descending (highest first)
 * Uses score field (overall composite score) from comparison
 * Tie-break: Title A-Z, then recent
 */
export function byWinnerScoreDesc(
  a: Comparison & { score?: number | null },
  b: Comparison & { score?: number | null }
): number {
  const scoreA = a.score ?? -Infinity;
  const scoreB = b.score ?? -Infinity;

  if (scoreB !== scoreA) {
    return scoreB - scoreA;
  }

  // Tie-break: Title A-Z
  return byTitleAZ(a, b);
}

/**
 * Group favorites first, then by createdAt desc within each group
 * Tie-break: Title A-Z
 */
export function groupFavoritesThen(
  comparisons: Comparison[],
  favorites: Set<string>
): Comparison[] {
  const favorited = comparisons.filter(c => favorites.has(c.id));
  const notFavorited = comparisons.filter(c => !favorites.has(c.id));

  // Sort each group by recent first
  favorited.sort(byCreatedAtDesc);
  notFavorited.sort(byCreatedAtDesc);

  return [...favorited, ...notFavorited];
}

/**
 * Get the appropriate comparator for a sort option
 */
export function getComparator(
  sortOption: ComparisonSortOption,
  favorites?: Set<string>
): (a: Comparison, b: Comparison) => number | null {
  switch (sortOption) {
    case 'recent':
      return byCreatedAtDesc;
    case 'oldest':
      return byCreatedAtAsc;
    case 'title':
      return byTitleAZ;
    case 'score':
      return byWinnerScoreDesc;
    case 'favorites':
      // Favorites requires special handling (grouping), return null
      return null as any;
    default:
      return byCreatedAtDesc;
  }
}

/**
 * Apply sort to comparisons array
 */
export function sortComparisons(
  comparisons: Comparison[],
  sortOption: ComparisonSortOption,
  favorites?: Set<string>
): Comparison[] {
  if (sortOption === 'favorites' && favorites) {
    return groupFavoritesThen(comparisons, favorites);
  }

  const comparator = getComparator(sortOption, favorites);
  if (!comparator) {
    return comparisons;
  }

  return [...comparisons].sort(comparator);
}
